import {
  DEFAULT_COMPANY_COST, DEFAULT_TAX_CONFIG, LEGACY_WORK_CONFIG,
  getMgmtFeePerPerson, isProbationMonth, calcPayrollDeductions,
} from "../firebase/schema";

// Bảng lương tháng dạng đầy đủ: target → thực đạt → chi tiết lương & thưởng → gross
// → khấu trừ của người lao động → chi phí công ty (BH, phí dịch vụ, VAT) → tổng chi.
// Bố cục theo file agency: mỗi nhóm cột một màu nền để đọc nhanh.

const FILL = {
  info: "FFF2F2F2",     // xám — thông tin nhận dạng
  target: "FFF8CBAD",   // cam — target giao
  result: "FFE2EFDA",   // xanh lá — kết quả thực đạt
  salary: "FFFFF2CC",   // vàng — các khoản cấu thành lương
  deduct: "FFFCE4D6",   // cam nhạt — khấu trừ của người lao động
  cost: "FFDDEBF7",     // xanh dương — chi phí công ty
  total: "FFF8CBD8",    // hồng — tổng chi phí
};

const thin = { style: "thin", color: { argb: "FFBFBFBF" } };
const border = { top: thin, left: thin, bottom: thin, right: thin };
const solid = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

const MONEY = "#,##0";
const PCT = "0%";

// Cột được định danh bằng key — thêm/bớt cột không làm lệch định dạng hay dòng tổng
// sum: cộng ở dòng TỔNG · usd: quy đổi ở dòng USD
const COLUMNS = [
  { key: "year",      label: "Year",            group: "info",   width: 7 },
  { key: "quarter",   label: "Quarter",         group: "info",   width: 8 },
  { key: "month",     label: "Month",           group: "info",   width: 8 },
  { key: "name",      label: "Promoter Name",   group: "info",   width: 22, left: true },
  { key: "shop",      label: "Store handle",    group: "info",   width: 16, left: true },
  { key: "days",      label: "Ngày công",       group: "info",   width: 9,  fmt: "0.0", sum: true },

  { key: "soTarget",  label: "Sellout Target",  group: "target", width: 10, fmt: "0", sum: true },
  { key: "heTarget",  label: "High-end Target", group: "target", width: 11, fmt: "0", sum: true },

  { key: "soResult",  label: "Sellout Result",  group: "result", width: 10, fmt: "0", sum: true },
  { key: "soHR",      label: "Sellout H/R",     group: "result", width: 9,  fmt: PCT },
  { key: "heResult",  label: "High-end Result", group: "result", width: 11, fmt: "0", sum: true },
  { key: "heHR",      label: "High-end H/R",    group: "result", width: 10, fmt: PCT },

  { key: "workPay",   label: "Fixed Salary (incl. holidays)",          group: "salary", width: 13, fmt: MONEY, sum: true, usd: true },
  { key: "holHours",  label: "Holiday hours",         group: "salary", width: 8,  fmt: "0.0", sum: true },
  { key: "holOt",     label: "Holiday OT (300%)",     group: "salary", width: 12, fmt: MONEY, sum: true, usd: true },
  { key: "otHours",   label: "OT hours",              group: "salary", width: 8,  fmt: "0.0", sum: true },
  { key: "otPay",     label: "OT pay (150%)",         group: "salary", width: 12, fmt: MONEY, sum: true, usd: true },
  { key: "woHours",   label: "Rest-day hours",        group: "salary", width: 8,  fmt: "0.0", sum: true },
  { key: "woPay",     label: "Rest-day OT (200%)",    group: "salary", width: 12, fmt: MONEY, sum: true, usd: true },
  { key: "allowance", label: "Lunch allowance",       group: "salary", width: 13, fmt: MONEY, sum: true, usd: true },
  { key: "soBonus",   label: "Incentive (SO target)", group: "salary", width: 14, fmt: MONEY, sum: true, usd: true },
  { key: "heBonus",   label: "Incentive (High-end)",  group: "salary", width: 14, fmt: MONEY, sum: true, usd: true },
  { key: "program",   label: "Program bonus",         group: "salary", width: 13, fmt: MONEY, sum: true, usd: true },
  { key: "event",     label: "Event bonus",           group: "salary", width: 12, fmt: MONEY, sum: true, usd: true },
  { key: "otManual",  label: "OT (non-taxable, adj)", group: "salary", width: 12, fmt: MONEY, sum: true, usd: true },
  { key: "gross",     label: "Gross Income",          group: "salary", width: 14, fmt: MONEY, sum: true, usd: true, bold: true },

  { key: "empIns",    label: "BH nhân viên đóng",     group: "deduct", width: 13, fmt: MONEY, sum: true, usd: true, red: true },
  { key: "nonTax",    label: "Không chịu thuế",       group: "deduct", width: 13, fmt: MONEY, sum: true, usd: true },
  { key: "pit",       label: "Thuế TNCN",             group: "deduct", width: 12, fmt: MONEY, sum: true, usd: true, red: true },
  { key: "net",       label: "Net salary",            group: "deduct", width: 14, fmt: MONEY, sum: true, usd: true, net: true },

  { key: "compIns",   label: "Insurances (công ty)",  group: "cost",   width: 14, fmt: MONEY, sum: true, usd: true, red: true },
  { key: "service",   label: "Service fee",           group: "cost",   width: 12, fmt: MONEY, sum: true, usd: true, red: true },
  { key: "vat",       label: "VAT",                   group: "cost",   width: 12, fmt: MONEY, sum: true, usd: true, red: true },

  { key: "expense",   label: "Total Salary Expenses (incl. VAT)", group: "total", width: 16, fmt: MONEY, sum: true, usd: true },
];

export async function exportSalaryMatrixXLSX({
  rows, month, costCfg = DEFAULT_COMPANY_COST, taxCfg = DEFAULT_TAX_CONFIG,
  workCfg = LEGACY_WORK_CONFIG, insAdjs = [],
}) {
  const ExcelJS = (await import("exceljs")).default;

  const cc = { ...DEFAULT_COMPANY_COST, ...(costCfg || {}) };
  const tc = { ...DEFAULT_TAX_CONFIG, ...(taxCfg || {}) };
  const wc = { ...LEGACY_WORK_CONFIG, ...(workCfg || {}) };
  const [year, mon] = month.split("-").map(Number);

  const insAdjMap = {};
  insAdjs.forEach(a => { insAdjMap[a.promoterId] = a; });

  // Phí quản lý tính trên số người CÓ CÔNG trong tháng — cùng quy tắc với card chi phí trên web
  const staffCount = rows.filter(r => r.dayCreditTotal > 0).length;
  const feePer = getMgmtFeePerPerson(staffCount, cc.mgmtTiers);

  const data = rows.map(r => {
    const adj = insAdjMap[r.uid];
    const probation = isProbationMonth(r.startDate, month, tc.probationMonths);
    const insured = adj?.enabled != null ? adj.enabled : !probation;
    const insBase = insured ? (adj?.baseOverride != null ? Number(adj.baseOverride) : Number(r.baseSalary || 0)) : 0;

    // PAI (BH tai nạn) tính theo đầu người có công, kể cả người thử việc — chỉ các khoản % mới miễn
    const compIns =
      insBase * (cc.siPct + cc.hiPct + cc.uiPct + cc.unionPct) / 100 +
      (r.dayCreditTotal > 0 && adj?.enabled !== false ? Number(cc.pai) || 0 : 0);
    const service = r.dayCreditTotal > 0 ? feePer : 0;

    const ded = calcPayrollDeductions({
      totalIncome: r.total, baseSalary: r.baseSalary || 0,
      allowance: r.allowance || 0, dependents: r.dependents || 0,
      nonTaxable: r.nonTaxable ?? r.otNonTax ?? 0, startDate: r.startDate || null,
      month, cfg: tc, insuranceEnabled: insured,
    });
    const vat = (r.total + compIns + service) * (cc.vatPct || 0) / 100;
    const soTarget = r.target?.sellTarget || 0;
    const heTarget = r.target?.highendTarget || 0;

    return {
      year, quarter: `Q${Math.ceil(mon / 3)}`, month: `M${String(mon).padStart(2, "0")}`,
      name: r.name, shop: r.shopName || "", days: r.dayCreditTotal || 0,
      soTarget, heTarget,
      soResult: r.sold || 0, soHR: soTarget > 0 ? r.sold / soTarget : 0,
      heResult: r.highendSold || 0, heHR: heTarget > 0 ? r.highendSold / heTarget : 0,
      workPay: r.workPay || 0, holHours: r.holidayOtHours || 0, holOt: r.holidayOtPay || 0,
      otHours: r.otHours || 0, otPay: r.otPay || 0,
      woHours: r.weeklyOffHours || 0, woPay: r.weeklyOffPay || 0,
      allowance: r.allowance || 0, soBonus: r.salesBonus || 0, heBonus: r.highendBonus || 0,
      program: r.bonusProgram || 0, event: r.eventBonus || 0, otManual: r.otNonTax || 0,
      gross: r.total || 0,
      empIns: ded.empTotal, nonTax: ded.nonTaxable || 0, pit: ded.pit, net: ded.net,
      compIns: Math.round(compIns), service, vat: Math.round(vat),
      expense: Math.round(r.total + compIns + service + vat),
    };
  });

  const wb = new ExcelJS.Workbook();
  wb.creator = "PromoterHQ";
  wb.created = new Date();
  const ws = wb.addWorksheet(`Luong ${month}`, {
    views: [{ state: "frozen", xSplit: 5, ySplit: 1 }],
  });

  // ── Header ──
  const head = ws.addRow(COLUMNS.map(c => c.label));
  head.height = 34;
  head.eachCell({ includeEmpty: true }, (cell, i) => {
    cell.fill = solid(FILL[COLUMNS[i - 1].group]);
    cell.font = { bold: true, size: 10 };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = border;
  });

  // ── Dữ liệu ──
  data.forEach(d => {
    const row = ws.addRow(COLUMNS.map(c => d[c.key]));
    row.height = 18;
    row.eachCell({ includeEmpty: true }, (cell, i) => {
      const c = COLUMNS[i - 1];
      cell.border = border;
      if (c.fmt) cell.numFmt = c.fmt;
      cell.alignment = { horizontal: c.left ? "left" : "center", vertical: "middle" };
      cell.font = c.net ? { size: 10, bold: true, color: { argb: "FF1D9E75" } }
        : c.group === "total" ? { size: 10, bold: true, color: { argb: "FFC8152A" } }
        : c.red ? { size: 10, color: { argb: "FFA32D2D" } }
        : { size: 10, bold: !!c.bold };
    });
  });

  // ── Dòng tổng ──
  const sumOf = (key) => data.reduce((a, d) => a + (Number(d[key]) || 0), 0);
  const totalRow = ws.addRow(COLUMNS.map((c, i) => (i === 0 ? "TỔNG" : c.sum ? sumOf(c.key) : "")));
  totalRow.height = 22;
  totalRow.eachCell({ includeEmpty: true }, (cell, i) => {
    const c = COLUMNS[i - 1];
    cell.border = { ...border, top: { style: "medium", color: { argb: "FF808080" } } };
    cell.font = { size: 10, bold: true };
    cell.fill = solid("FFFAFAFA");
    if (c.fmt && c.fmt !== PCT) cell.numFmt = c.fmt;
    cell.alignment = { horizontal: "center", vertical: "middle" };
  });

  // ── Dòng quy đổi USD ──
  const rate = Number(cc.usdRate) || 0;
  if (rate > 0) {
    const usdRow = ws.addRow(COLUMNS.map((c, i) =>
      i === 0 ? `USD (${rate.toLocaleString("vi-VN")}đ)` : c.usd ? sumOf(c.key) / rate : ""));
    usdRow.height = 18;
    usdRow.eachCell({ includeEmpty: true }, (cell, i) => {
      cell.border = border;
      cell.font = { size: 9, italic: true, color: { argb: "FF666666" } };
      if (COLUMNS[i - 1].usd) cell.numFmt = "#,##0.00";
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });
  }

  COLUMNS.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; });
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: COLUMNS.length } };

  // ── Ghi chú cách tính (đọc từ cấu hình đang áp dụng cho tháng này) ──
  const vnd = (n) => Number(n).toLocaleString("vi-VN");
  const noteRow = ws.rowCount + 2;
  const notes = [
    "Ghi chú:",
    wc.legacy
      ? `• Tháng này tính theo quy tắc cũ: ca ${wc.standardHours}h, ngày lễ có đi làm ×${wc.holidayOtRate / 100} nằm trong Fixed Salary, không có OT.`
      : `• Ngày công chuẩn ${wc.standardDays} ngày, ca ${wc.standardHours}h. Đơn giá giờ = lương cơ bản ÷ ${wc.standardDays} ÷ ${wc.rateHours || 8}. Fixed Salary gồm cả ${wc.holidayPaidRate}% lương mỗi ngày lễ (đi làm hay không); giờ làm ngày lễ đã đăng ký được cộng thêm ${wc.holidayOtRate}% ở cột Holiday OT.`,
    !wc.legacy && `• Tăng ca theo Điều 98 BLLĐ: ngày thường ${wc.otRate}%, ngày nghỉ hằng tuần ${wc.weeklyOffOtRate}%, ngày lễ/Tết/nghỉ có lương ${wc.holidayOtRate}% (mọi giờ làm ngày lễ) — chỉ tính giờ đã đăng ký và được duyệt; giờ promoter đăng ký không vượt giờ chấm công, giờ admin nhập dùng đúng số nhập. Ngày nghỉ tuần đi làm không tính là công thường.${wc.otTaxExempt ? " Toàn bộ tiền tăng ca miễn thuế TNCN (Luật TNCN 2025)." : ""}`,
    `• Gross Income = tất cả các khoản ở nhóm cột vàng.`,
    `• BH nhân viên đóng = ${tc.empSiPct}% + ${tc.empHiPct}% + ${tc.empUiPct}% trên lương cơ bản. Người thử việc không đóng.`,
    tc.probationTaxMode === "flat" && `• Người thử việc: khấu trừ ${tc.probationFlatRate}% trên tổng Gross (kể cả tăng ca), không giảm trừ gia cảnh, từ ${vnd(tc.probationFlatMin)}đ trở lên.`,
    `• Thuế TNCN: lũy tiến từng phần; trừ ăn ca tối đa ${vnd(tc.mealAllowanceCap)}đ và các khoản không chịu thuế; giảm trừ bản thân ${vnd(tc.personalDeduction)}đ + ${vnd(tc.dependentDeduction)}đ/người phụ thuộc${tc.probationTaxMode === "flat" ? "" : " (thử việc không được giảm trừ)"}.`,
    `• Insurances (công ty) = ${cc.siPct}% + ${cc.hiPct}% + ${cc.uiPct}% + ${cc.unionPct}% trên lương đóng BH, cộng PAI ${vnd(cc.pai)}đ/người.`,
    `• Service fee = ${vnd(feePer)}đ/người (${staffCount} người có công trong tháng).`,
    `• VAT ${cc.vatPct}% tính trên Gross + Insurances + Service fee.`,
  ].filter(Boolean);
  notes.forEach((line, i) => {
    const cell = ws.getCell(noteRow + i, 1);
    cell.value = line;
    cell.font = { size: 10, bold: i === 0, color: { argb: i === 0 ? "FF000000" : "FF666666" } };
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `luong_${month}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
